import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	getUserDetails,
	setUserDetails,
	setToken,
	getToken,
	set2faToken,
	removeToken,
	remove2faToken,
	get2faToken,
} from '../utils/helpers';
import {
	AuthResponse,
	ImpersonateResponse,
	Role,
	ValidateOtpResponse,
} from '../modules/Auth/types/Auth';
import { AuthRepository } from '../modules/Auth/repository/Auth';

// -------------------------
// LOGOUT
// -------------------------
export const logoutUser = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
	try {
		const mfaToken = await get2faToken();
		if (mfaToken) {
			await AuthRepository.invalidateToken(mfaToken);
		}
		removeToken();
		remove2faToken();
		setUserDetails(null);
		return true;
	} catch (error) {
		return rejectWithValue('Failed to logout: ' + error);
	}
});

// -------------------------
// IMPERSONATE USER
// -------------------------
export const impersonateUser = createAsyncThunk(
	'auth/impersonate',
	async (payload: Record<string, string | number>, { rejectWithValue }) => {
		try {
			const response = await AuthRepository.impersonate(payload);
			return response as ImpersonateResponse;
		} catch (err) {
			return rejectWithValue('Failed to impersonate: ' + err);
		}
	},
);

// -------------------------
// TYPES
// -------------------------
export interface LoginInfo {
	auth?: AuthResponse;
	mfaData?: ValidateOtpResponse;
	has2fa?: boolean;
}

export interface AuthSlice {
	user: AuthResponse | null;
	authLoading: boolean;
	token?: string;
	globalMsg?: string;
	isAuthenticated: boolean;
	roles?: Role[];
	mfaData?: LoginInfo;
	impersonating?: boolean;
	impersonatedUser?: AuthResponse | null;
	impersonateUsername?: string | null;
}

// -------------------------
// INITIAL STATE
// -------------------------
const storedUser = getUserDetails() as AuthResponse | null;

const initialState: AuthSlice = {
	user: storedUser,
	authLoading: false,
	token: await getToken(),
	isAuthenticated: !!getToken(),
	roles: storedUser?.roles ?? [],
	impersonating: !!storedUser?.impersonateUsername,
	impersonateUsername: storedUser?.impersonateUsername ?? null,
	impersonatedUser: storedUser?.originalAdmin ?? null,
};

// -------------------------
// SLICE
// -------------------------
const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		setUser: (state, action: PayloadAction<AuthResponse>) => {
			state.user = action.payload;
			setUserDetails(action.payload);
		},
		setAuthLoading: (state, action: PayloadAction<boolean>) => {
			state.authLoading = action.payload;
		},
		login2fa: (state, action: PayloadAction<LoginInfo>) => {
			state.mfaData = { ...state.mfaData, ...action.payload };
			if (action.payload.auth?.base64EncodedAuthenticationKey) {
				setToken(action.payload.auth?.base64EncodedAuthenticationKey ?? '');
			}
		},
		login: (state, action: PayloadAction<LoginInfo>) => {
			const prevUser = getUserDetails();

			state.token = action.payload.auth?.base64EncodedAuthenticationKey;
			state.isAuthenticated = true;
			state.authLoading = false;
			state.mfaData = action.payload;
			state.user = action.payload.auth || null;
			state.roles = action.payload.auth?.roles;
			state.impersonating = !!prevUser?.impersonateUsername;
			state.impersonateUsername = prevUser?.impersonateUsername ?? null;

			// Preserve impersonation context if it exists
			const mergedUser = {
				...state.user,
				originalAdmin: prevUser?.originalAdmin ?? prevUser,
				impersonateUsername: prevUser?.impersonateUsername,
				message: prevUser?.message,
			};

			setUserDetails(mergedUser);
			setToken(action.payload.auth?.base64EncodedAuthenticationKey ?? '');
			if (action.payload.mfaData?.token) {
				set2faToken(action.payload.mfaData.token);
			}
		},

		logout: state => {
			state.user = null;
			state.token = undefined;
			state.isAuthenticated = false;
			state.impersonating = false;
			state.impersonatedUser = null;
			state.impersonateUsername = null;
			removeToken();
			remove2faToken();
			setUserDetails(null);
		},
		endImpersonation: state => {
			const adminUser = getUserDetails()?.originalAdmin;
			if (adminUser) {
				state.user = adminUser;
				state.impersonating = false;
				state.impersonateUsername = null;
				state.impersonatedUser = null;
				setUserDetails(adminUser);
			}
		},
		setImpersonateData: (state, action: PayloadAction<ImpersonateResponse>) => {
			state.impersonating = true;
			state.isAuthenticated = true;
			state.impersonateUsername = action.payload.impersonate_username ?? null;

			const currentUser = getUserDetails();

			if (currentUser) {
				const sanitizedAdmin = { ...currentUser };
				sanitizedAdmin.originalAdmin = null;
				delete sanitizedAdmin.originalAdmin;

				setUserDetails({
					...currentUser,
					originalAdmin: sanitizedAdmin,
					impersonateUsername: action.payload.impersonate_username,
					message: action.payload.message,
				});
			}
		},
	},
	extraReducers: builder => {
		// --- Logout Thunk
		builder
			.addCase(logoutUser.pending, state => {
				state.authLoading = true;
			})
			.addCase(logoutUser.fulfilled, state => {
				state.user = null;
				state.mfaData = undefined;
				state.token = undefined;
				state.isAuthenticated = false;
				state.authLoading = false;
			})
			.addCase(logoutUser.rejected, (state, action) => {
				state.authLoading = false;
				state.globalMsg = action.payload as string;
			});

		// --- Impersonation Thunk
		builder
			.addCase(impersonateUser.pending, state => {
				state.authLoading = true;
			})
			.addCase(impersonateUser.fulfilled, (state, action) => {
				const { impersonate_username, message } = action.payload as ImpersonateResponse;

				state.authLoading = false;
				state.impersonating = true;
				state.isAuthenticated = true;
				state.impersonateUsername = impersonate_username ?? null;
				state.impersonatedUser = state.user;
				setUserDetails({
					...getUserDetails(),
					originalAdmin: getUserDetails(),
					impersonateUsername: impersonate_username,
					message,
				});
			})
			.addCase(impersonateUser.rejected, (state, action) => {
				state.authLoading = false;
				state.globalMsg = action.payload as string;
			});
	},
});

export const {
	setUser,
	setAuthLoading,
	login,
	login2fa,
	logout,
	endImpersonation,
	setImpersonateData,
} = authSlice.actions;

export default authSlice.reducer;
